import * as React from "react";
import {
    Table,
    TableBody,
    TablePagination,
    TableRow,
    TableCell,
    TableContainer,
    Grid,
    Typography,
    Paper,
    IconButton,
} from "@mui/material";
import { useTableOperations } from "./hooks/useTableOperations";
import { EnhancedTableHead } from "./components/EnhancedTableHead";
import { useStyles } from "./MainTable.styles";
import { useTranslation } from "react-i18next";
import { IMainTable, IMainTableColumn } from "./MainTableInterfaces";
import { useTheme } from "@mui/material";
import { IFailureCode } from "contexts/HMICachedDataContext/HMICachedDataContextInterfaces";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

const MainTableRaw = ({
    columns,
    rows,
    onRowClick,
    onExpandedRowClick,
    loading,
    customStyle,
    pagination,
    customStyleTypography,
    paperCustomStyle,
    tableRowCustomStyle,
    tableCellCustomStyle,
    tableExpandedRowCustomStyle,
    headerAlign = "left",
    hmiShiftDetailsRowNoFailureCodeStyle,
    paperElevation,
    maxHeight = "450px",
    count,
    tableOperationsValues,
    querySorted,
    handleRequestSortParent,
    handleChangeRowsPerPageParent,
    handleChangePageParent,
}: IMainTable) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const themeStyles = useTheme();

    const [expandedRows, setExpandedRows] = React.useState<number[]>([]);

    const [tbodyHeight, setTbodyHeight] = React.useState<number | null>(null);
    const tbodyRef = React.useRef<HTMLTableSectionElement>(null);

    React.useEffect(() => {
        if (tbodyRef.current) {
            if (tbodyRef.current.getBoundingClientRect().height !== 0) {
                setTbodyHeight(tbodyRef.current.getBoundingClientRect().height);
            }
        }
    }, [tableOperationsValues?.orderBy, tbodyHeight, rows]);

    const {
        getComparator,
        handleRequestSort,
        handleChangeRowsPerPage,
        handleChangePage,
        order,
        orderBy,
        page,
        rowsPerPage,
        stableSort,
    } = useTableOperations();

    const handleOnClickRow = (row: Object) => {
        if (onRowClick) {
            onRowClick(row);
        }
    };

    const handleOnClickRowExpanded = (row: Object) => {
        if (onExpandedRowClick) {
            onExpandedRowClick(row);
        }
    };

    const memoizedTableOperationsValues = React.useMemo(
        () => ({
            order: tableOperationsValues ? tableOperationsValues.order : order,
            orderBy: tableOperationsValues ? tableOperationsValues.orderBy : orderBy,
            page: tableOperationsValues ? tableOperationsValues.page : page,
            rowsPerPage: tableOperationsValues ? tableOperationsValues.rowsPerPage : rowsPerPage,
        }),
        [tableOperationsValues, order, orderBy, page, rowsPerPage],
    );

    const handleOnClickExpandRow = (row: Object, index: number) => {
        if (row.hasOwnProperty("expandedData")) {
            if (expandedRows.includes(index)) {
                setExpandedRows(expandedRows.filter((expandedRow) => expandedRow !== index));
            } else {
                setExpandedRows([...expandedRows, index]);
            }
        }
    };

    const expandedDataOnTable = React.useMemo(() => rows.some((row) => row.hasOwnProperty("expandedData")), [rows]);

    columns = React.useMemo(
        () =>
            expandedDataOnTable
                ? [
                      {
                          id: "expand_icon_dropdown_cell",
                          label: "",
                          hideSortIcon: true,
                          customStyles: { width: "1%" },
                          render: (row) => "",
                      },
                      ...columns,
                  ]
                : columns,
        [expandedDataOnTable, columns],
    );

    return (
        <>
            <Paper className={`${classes.root} ${paperCustomStyle}`} elevation={paperElevation}>
                <TableContainer
                    className={`${classes.tableContainer} ${customStyle}`}
                    sx={{ maxHeight: maxHeight ? maxHeight : "initial" }}
                >
                    <Table stickyHeader aria-label="sticky table">
                        <EnhancedTableHead
                            headerAlign={headerAlign}
                            headCells={columns}
                            order={memoizedTableOperationsValues.order}
                            orderBy={memoizedTableOperationsValues.orderBy}
                            onRequestSort={handleRequestSortParent ? handleRequestSortParent : handleRequestSort}
                            tableCellCustomStyle={tableCellCustomStyle}
                        />
                        <TableBody ref={tbodyRef}>
                            {rows.length > 0 &&
                                (querySorted
                                    ? rows
                                    : stableSort(
                                          rows,
                                          getComparator(
                                              memoizedTableOperationsValues.order,
                                              memoizedTableOperationsValues.orderBy,
                                          ),
                                      )
                                )
                                    .slice(
                                        ...(pagination && !tableOperationsValues
                                            ? [
                                                  memoizedTableOperationsValues.page *
                                                      memoizedTableOperationsValues.rowsPerPage,
                                                  memoizedTableOperationsValues.page *
                                                      memoizedTableOperationsValues.rowsPerPage +
                                                      memoizedTableOperationsValues.rowsPerPage,
                                              ]
                                            : []),
                                    )
                                    .map((row: Object, index: number) => {
                                        const rowWithFailureCodeForHMIShiftDetailsTable = row as unknown as {
                                            failure_code?: IFailureCode;
                                        };

                                        const rowWithExpandedData = row as unknown as {
                                            expandedData?: Object[];
                                        };

                                        return (
                                            <React.Fragment key={`${index}_${Math.random()}-mainRow`}>
                                                <TableRow
                                                    className={tableRowCustomStyle}
                                                    hover
                                                    role="checkbox"
                                                    tabIndex={-1}
                                                    key={`${index}_${Math.random()}`}
                                                    onClick={() => handleOnClickRow(row)}
                                                    sx={{
                                                        backgroundColor: hmiShiftDetailsRowNoFailureCodeStyle
                                                            ? rowWithFailureCodeForHMIShiftDetailsTable?.failure_code
                                                                ? "inherit"
                                                                : `${themeStyles.palette.error.lighter} !important`
                                                            : "inherit",
                                                        ...((row.hasOwnProperty("expandedData") || onRowClick) && {
                                                            cursor: "pointer",
                                                        }),
                                                    }}
                                                >
                                                    <>
                                                        {columns.map((column: IMainTableColumn, columnIndex) => {
                                                            return (
                                                                <TableCell
                                                                    key={column.id}
                                                                    align={column.align}
                                                                    style={{
                                                                        ...column.customStyles,
                                                                        ...(expandedRows.includes(index) && {
                                                                            borderBottom: "none",
                                                                        }),
                                                                    }}
                                                                    className={
                                                                        !!rowWithExpandedData?.expandedData &&
                                                                        columnIndex === 0
                                                                            ? classes.contentTableCellExpanded
                                                                            : tableCellCustomStyle
                                                                    }
                                                                >
                                                                    <div className={classes.tableCellWrapperExpanded}>
                                                                        {!!rowWithExpandedData?.expandedData &&
                                                                            columnIndex === 0 && (
                                                                                <IconButton
                                                                                    sx={{ padding: 0 }}
                                                                                    onClick={(event) => {
                                                                                        event.stopPropagation();
                                                                                        handleOnClickExpandRow(
                                                                                            row,
                                                                                            index,
                                                                                        );
                                                                                    }}
                                                                                >
                                                                                    <KeyboardArrowDownIcon
                                                                                        fontSize="small"
                                                                                        className={classes.arrowIcon}
                                                                                        style={{
                                                                                            transform:
                                                                                                expandedRows.includes(
                                                                                                    index,
                                                                                                )
                                                                                                    ? "rotate(180deg)"
                                                                                                    : "rotate(0deg)",
                                                                                        }}
                                                                                    />
                                                                                </IconButton>
                                                                            )}
                                                                        <Typography
                                                                            component="span"
                                                                            className={classes.typography}
                                                                            style={customStyleTypography}
                                                                        >
                                                                            {column.render(row)}
                                                                        </Typography>
                                                                    </div>
                                                                </TableCell>
                                                            );
                                                        })}
                                                    </>
                                                </TableRow>
                                                {!!rowWithExpandedData?.expandedData &&
                                                    expandedRows.includes(index) &&
                                                    rowWithExpandedData?.expandedData.map(
                                                        (expandedRow: Object, index: number) => {
                                                            return (
                                                                <TableRow
                                                                    className={`${tableRowCustomStyle} ${tableExpandedRowCustomStyle} ${
                                                                        index ===
                                                                        (rowWithExpandedData?.expandedData?.length ||
                                                                            0) -
                                                                            1
                                                                            ? classes.lastExpandedRow
                                                                            : classes.tableExpandedRow
                                                                    }`}
                                                                    onClick={() =>
                                                                        handleOnClickRowExpanded(expandedRow)
                                                                    }
                                                                    sx={{
                                                                        ...(onExpandedRowClick && {
                                                                            cursor: "pointer",
                                                                        }),
                                                                    }}
                                                                    hover
                                                                    role="checkbox"
                                                                    tabIndex={-1}
                                                                    key={`${index}_${Math.random()}`}
                                                                >
                                                                    <>
                                                                        {columns.map((column: IMainTableColumn) => {
                                                                            return (
                                                                                <TableCell
                                                                                    key={column.id}
                                                                                    align={column.align}
                                                                                    style={{ ...column.customStyles }}
                                                                                    className={tableCellCustomStyle}
                                                                                >
                                                                                    <Typography
                                                                                        component="span"
                                                                                        className={classes.typography}
                                                                                        style={customStyleTypography}
                                                                                    >
                                                                                        {column.renderExpandedRow?.(
                                                                                            expandedRow,
                                                                                        ) ?? column.render(expandedRow)}
                                                                                    </Typography>
                                                                                </TableCell>
                                                                            );
                                                                        })}
                                                                    </>
                                                                </TableRow>
                                                            );
                                                        },
                                                    )}
                                                {!!rowWithExpandedData?.expandedData &&
                                                    expandedRows.includes(index) &&
                                                    rowWithExpandedData?.expandedData.length === 0 && (
                                                        <TableCell colSpan={columns.length}>
                                                            <Typography className={classes.textCenter} variant="h4">
                                                                {t("data.no_data")}
                                                            </Typography>
                                                        </TableCell>
                                                    )}
                                            </React.Fragment>
                                        );
                                    })}
                            {rows.length === 0 && !loading && (
                                <TableRow>
                                    <TableCell colSpan={columns.length}>
                                        <Typography className={classes.textCenter} variant="h4">
                                            {t("data.no_data")}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                    {loading && tbodyHeight !== null && (
                        <Grid container justifyContent="center" alignItems="center" style={{ height: tbodyHeight }}>
                            {t("data.loading")}
                        </Grid>
                    )}
                </TableContainer>
                {pagination && (
                    <TablePagination
                        rowsPerPageOptions={[5, 10, 25]}
                        component="div"
                        count={count ? count : rows.length}
                        rowsPerPage={memoizedTableOperationsValues.rowsPerPage}
                        page={memoizedTableOperationsValues.page}
                        onPageChange={handleChangePageParent ? handleChangePageParent : handleChangePage}
                        onRowsPerPageChange={
                            handleChangeRowsPerPageParent ? handleChangeRowsPerPageParent : handleChangeRowsPerPage
                        }
                    />
                )}
            </Paper>
        </>
    );
};

export const MainTable = React.memo(MainTableRaw);
